import { useState, useContext } from 'react';
import {
    Box,
    Chip,
    Avatar,
    Button,
    TextField,
    Stack,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { PaymentOutlined, Add, Search } from '@mui/icons-material';
import { format } from 'date-fns';
import { es, enUS, ca, pt, de, fr } from 'date-fns/locale';

const localeMap: Record<string, typeof es> = {
    es,
    en: enUS,
    ca,
    pt,
    de,
    fr,
    mx: es,
};
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttTable,
    SttChipUser,
    SttTopControls,
} from '../../../sporttia/all';
import PaymentModal from '../../../components/paymentModal/PaymentModal';
import PaymentCreationDialog from '../../../components/dialogs/PaymentCreationDialog';
import { useToggle } from '../../../lib/hooks';
import { commaSeparated } from '../../../lib/utils';
import PayMultiplePaymentsDialog from '../../../components/dialogs/PayMultiplePaymentsDialog';
import { getPaymentForm } from '../../../lib/paymentHelper';
import DateBuilder from '../../../lib/DateBuilder';
import { PaymentForm } from '../../../types/payment';

type MshipProp = {
    id: number;
    name?: string;
    user?: {
        id: number;
    };
};

type UserData = {
    id: number;
    login?: string;
    name?: string;
};

type Props = {
    mship: MshipProp;
};

type FilterState = {
    concept: string;
    status: string;
    subpayments: boolean;
    useIni: string;
    useEnd: string;
    paidIni: string;
    paidEnd: string;
    paymentForms: string[];
    modules: string[];
};

type PaginationParams = {
    page: number;
    rows: number;
};

type PaymentData = {
    id: number;
    created: string;
    creator: UserData;
    module: string;
    concept: string;
    ini: string;
    end: string;
    paymentDate?: string;
    collector?: UserData;
    paymentForm: PaymentForm;
    price: number;
    trash?: boolean;
    children?: {
        count: number;
    };
    parent?: unknown;
};

type PaymentsResponse = {
    rows?: PaymentData[];
    total?: number;
};

export default function MshipPayments({ mship }: Props): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [payments, setPayments] = useState<PaymentsResponse | undefined>();
    const [idPayModal, setIdPayModal] = useState<number | null>(null);
    const [creatingPayment, setCreatingPayment] = useState<boolean>(false);
    const currentYear = new Date().getFullYear();
    const [filters, setFilters] = useState<FilterState>({
        concept: '',
        status: '',
        subpayments: false,
        useIni: format(new Date(currentYear, 0, 1), 'yyyy-MM-dd 00:00:00'),
        useEnd: format(new Date(currentYear, 11, 31), 'yyyy-MM-dd 23:59:59'),
        paidIni: format(new Date(currentYear, 0, 1), 'yyyy-MM-dd 00:00:00'),
        paidEnd: format(new Date(currentYear, 11, 31), 'yyyy-MM-dd 23:59:59'),
        paymentForms: [],
        modules: [],
    });
    const [filterPayDateChecked, setFilterPayDateChecked] =
        useState<boolean>(false);
    const [filterUseDateChecked, setFilterUseDateChecked] =
        useState<boolean>(false);
    const [selectRows, setSelectRows] = useState<PaymentData[]>([]);
    const [paymentsIds, setPaymentsIds] = useState<number[] | null>(null);

    const [trash, toggleTrash] = useToggle(() => {
        loadPayments();
    });

    const loadPayments = (p: PaginationParams = { page: 1, rows: 25 }) => {
        const paramsFilters: Record<string, unknown> = {
            concept: filters.concept,
            status: filters.status,
            subpayments: +filters.subpayments,
            ...(filters.paymentForms.length > 0 && {
                paymentForms: commaSeparated(filters.paymentForms),
            }),
            ...(filters.modules.length > 0 && {
                modules: commaSeparated(filters.modules),
            }),
            ...(filterPayDateChecked && {
                paidIni: filters.paidIni,
                paidEnd: filters.paidEnd,
            }),
            ...(filterUseDateChecked && {
                useIni: filters.useIni,
                useEnd: filters.useEnd,
            }),
        };

        const params = {
            page: p.page,
            rows: p.rows,
            ...paramsFilters,
            trash,
        };

        cxt?.api('GET', `/mships/${mship.id}/payments`, {
            params,
            success: (r: PaymentsResponse) => {
                setPayments(r);
            },
        });
    };

    const openPayment = (pay: PaymentData) => {
        setIdPayModal(pay.id);
    };

    const onPaymentSuccess = () => {
        setCreatingPayment(false);
        loadPayments();
    };

    const handleCollectPayments = () => {
        setPaymentsIds(selectRows.map((payment: PaymentData) => payment.id));
    };

    return (
        <>
            {/* Top Bar: Search + Buttons */}
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <TextField
                    size="small"
                    label={t('Concept')}
                    variant="outlined"
                    value={filters.concept}
                    onChange={(e) =>
                        setFilters({ ...filters, concept: e.target.value })
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            loadPayments();
                        }
                    }}
                    placeholder={t('Concept')}
                    sx={{
                        maxWidth: 300,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => loadPayments()}
                                    edge="end"
                                    size="small"
                                >
                                    <Search />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => setCreatingPayment(true)}
                    >
                        {t('New')}
                    </Button>
                    {selectRows.length > 1 && (
                        // Contorno, no verde: unificar es una acción, y el
                        // verde del sistema significa «ha salido bien». Como
                        // «Nuevo» ya es la principal de la vista, ésta va en la
                        // variante de igual peso sin relleno.
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<PaymentOutlined />}
                            onClick={handleCollectPayments}
                        >
                            {t('unify')} ({selectRows.length})
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* Top Controls with Filters */}
            <SttTopControls
                mb={2}
                extraFields={[
                    // Row 1: Status (full width)
                    {
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        type: 'selectAutocomplete',
                        caption: t('Status'),
                        name: 'status',
                        value: filters.status,
                        labelField: 'label',
                        valueField: 'id',
                        options: cxt?.constants?.paymentStatuses || [],
                    },
                    // Row 2: Date of use + Date of payment (side by side)
                    {
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 6,
                        type: 'period',
                        caption: t('DateOfUse'),
                        name: 'useDate',
                        enabled: filterUseDateChecked,
                        nameIni: 'useIni',
                        valIni: filters.useIni,
                        nameEnd: 'useEnd',
                        valEnd: filters.useEnd,
                        onToggle: setFilterUseDateChecked,
                    },
                    {
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 6,
                        type: 'period',
                        caption: t('DateOfPayment'),
                        name: 'paidDate',
                        enabled: filterPayDateChecked,
                        nameIni: 'paidIni',
                        valIni: filters.paidIni,
                        nameEnd: 'paidEnd',
                        valEnd: filters.paidEnd,
                        onToggle: setFilterPayDateChecked,
                    },
                    // Row 3: Payment forms chips
                    {
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        name: 'paymentForms',
                        type: 'selectablechips',
                        caption: t('PaymentForm'),
                        items: cxt?.constants?.paymentForms?.map(
                            (pf: { label: string; id: string }) => ({
                                label: pf.label,
                                value: pf.id,
                            }),
                        ),
                        selected: filters.paymentForms,
                        chip: Chip,
                    },
                    // Row 4: Module chips
                    {
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        name: 'modules',
                        type: 'selectablechips',
                        caption: t('Module'),
                        items: cxt?.constants?.modules?.map(
                            (m: { label: string; id: string }) => ({
                                label: m.label,
                                value: m.id,
                            }),
                        ),
                        selected: filters.modules,
                        chip: Chip,
                    },
                    // Row 5: Other options
                    {
                        xs: 12,
                        sm: 12,
                        md: 12,
                        lg: 12,
                        type: 'checkmulti',
                        caption: t('OtherOptions'),
                        name: 'otherOptions',
                        children: [
                            {
                                name: 'subpayments',
                                caption: t('ChildPayments'),
                                checked: filters.subpayments,
                                onChange: ({
                                    name,
                                    value,
                                }: {
                                    name: string | undefined;
                                    value: unknown;
                                }) => {
                                    if (!name) return;
                                    setFilters({
                                        ...filters,
                                        [name]: value,
                                    });
                                },
                            },
                        ],
                    },
                ]}
                onChange={({
                    name,
                    value,
                }: {
                    name: string | undefined;
                    value: unknown;
                }) => {
                    if (!name) return;
                    if (name === 'useIni' || name === 'useEnd') {
                        const date = new Date(value as string);
                        const timeStr =
                            name === 'useIni' ? '00:00:00' : '23:59:59';
                        setFilters({
                            ...filters,
                            [name]: `${format(date, 'yyyy-MM-dd')} ${timeStr}`,
                        });
                    } else if (name === 'paidIni' || name === 'paidEnd') {
                        const date = new Date(value as string);
                        const timeStr =
                            name === 'paidIni' ? '00:00:00' : '23:59:59';
                        setFilters({
                            ...filters,
                            [name]: `${format(date, 'yyyy-MM-dd')} ${timeStr}`,
                        });
                    } else {
                        setFilters({ ...filters, [name]: value });
                    }
                }}
                onToggleTrash={toggleTrash}
                onFilter={loadPayments}
            />

            {/* Payments Table */}
            <SttTable
                data={payments}
                selectRows={selectRows}
                setSelectRows={setSelectRows}
                columns={[
                    {
                        field: 'created',
                        title: t('Created'),
                        width: 120,
                        value: (r: PaymentData) => (
                            <Stack spacing={1}>
                                <SttChipUser
                                    user={r.creator as { login: string }}
                                />
                                <Box
                                    sx={{
                                        fontSize: '0.875rem',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {new DateBuilder(
                                        r.created,
                                        cxt?.sc?.timezone?.name,
                                        cxt?.lang?.key,
                                    ).dmy()}
                                </Box>
                            </Stack>
                        ),
                    },
                    {
                        field: 'module',
                        title: t('Module'),
                        type: 'module',
                    },
                    {
                        field: 'concept',
                        title: t('Concept'),
                        align: 'left',
                        value: (r: PaymentData) => (
                            <Stack spacing={1}>
                                <Box
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openPayment(r);
                                    }}
                                    sx={{
                                        color: 'var(--brand-text)',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {r.concept}
                                </Box>

                                {r.children && (
                                    <Chip
                                        size="small"
                                        avatar={
                                            <Avatar>{r.children.count}</Avatar>
                                        }
                                        label={t('Children')}
                                    />
                                )}

                                {Boolean(r.parent) && (
                                    <Chip size="small" label={t('Father')} />
                                )}
                            </Stack>
                        ),
                    },
                    {
                        field: 'period',
                        title: t('Period'),
                        value: (r: PaymentData) => ({ ini: r.ini, end: r.end }),
                        type: 'period',
                    },
                    {
                        field: 'collector',
                        title: t('Paid'),
                        width: 120,
                        value: (r: PaymentData) =>
                            r.paymentDate &&
                            r.collector && (
                                <Stack spacing={1}>
                                    <SttChipUser
                                        user={r.collector as { login: string }}
                                    />
                                    <Box
                                        sx={{
                                            fontSize: '0.875rem',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {format(
                                            new Date(r.paymentDate),
                                            'd MMM yyyy',
                                            {
                                                locale:
                                                    localeMap[
                                                        cxt?.lang?.key || 'es'
                                                    ] || es,
                                            },
                                        )}
                                    </Box>
                                </Stack>
                            ),
                    },
                    {
                        field: 'paymentForm',
                        title: t('PForm'),
                        type: 'pf',
                        value: (row: PaymentData) =>
                            getPaymentForm({
                                trash: row.trash ?? false,
                                paymentForm: row.paymentForm as
                                    | ''
                                    | 'DATAFONO'
                                    | 'DATAFONO_CONNECTED'
                                    | 'CASH'
                                    | 'PURSE'
                                    | 'TPV'
                                    | 'LATER'
                                    | 'BANK'
                                    | 'FREE'
                                    | 'RECEIPT'
                                    | 'ZITYCARD'
                                    | 'CASHDRO',
                            }),
                    },
                    {
                        field: 'price',
                        title: t('Price'),
                        type: 'price',
                    },
                ]}
                onFetch={loadPayments}
                selection={{ actions: [] }}
            />

            {/* Payment Detail Modal */}
            {idPayModal && (
                <PaymentModal
                    idPayment={idPayModal}
                    disableUrlNavigation
                    onClose={() => {
                        loadPayments();
                        setIdPayModal(null);
                    }}
                />
            )}

            {/* Payment Creation Dialog */}
            <PaymentCreationDialog
                open={creatingPayment}
                mship={
                    mship as {
                        id?: number;
                        name: string;
                        fullName?: string;
                        user: { id?: number };
                    }
                }
                onClose={() => setCreatingPayment(false)}
                onPaymentSuccess={onPaymentSuccess}
            />

            {/* Multiple Payments Collection Dialog */}
            <PayMultiplePaymentsDialog
                paymentsIds={paymentsIds}
                userID={mship.user?.id}
                onClose={() => setPaymentsIds(null)}
                onCreate={(response: unknown) => {
                    const res = response as { payment?: { id: number } };
                    setIdPayModal(res?.payment?.id || null);
                    setPaymentsIds(null);
                }}
            />
        </>
    );
}
